import { type FormEvent, useState } from 'react';
import { PASSWORD_RULES } from '@pcp/contracts';
import { ApiRequestError } from '../api/client';
import { useChangePassword } from '../api/queries';
import { t } from '../i18n/pt-BR';

/** Lista as regras da política, marcando as que a senha já cumpre (a validação final é do BFF). */
export function PasswordRules({ senha, atual }: { senha: string; atual?: string }) {
  const rules = [
    ...PASSWORD_RULES.map((r) => ({ id: r.id, label: r.label, ok: r.test(senha) })),
    ...(atual !== undefined
      ? [{ id: 'different', label: t.password.differentFromCurrent, ok: senha.length > 0 && senha !== atual }]
      : []),
  ];
  return (
    <div className="rules" aria-live="polite">
      <div className="hint">{t.password.rulesTitle}</div>
      <ul>
        {rules.map((r) => (
          <li key={r.id} className={senha.length === 0 ? '' : r.ok ? 'ok' : 'bad'}>
            <span aria-hidden="true">{r.ok && senha.length > 0 ? '✓' : '○'}</span> {r.label}
            <span className="sr-only">{senha.length === 0 ? '' : r.ok ? ' (cumprida)' : ' (pendente)'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Troca da própria senha. Usada na tela "Segurança da conta" e na troca obrigatória (senha provisória).
 * A validação final é do BFF; aqui só o que evita uma ida ao servidor.
 */
export function PasswordForm({ onDone, submitLabel }: { onDone?: () => void; submitLabel?: string }) {
  const change = useChangePassword();
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [show, setShow] = useState(false);
  const [done, setDone] = useState(false);

  const rulesOk = PASSWORD_RULES.every((r) => r.test(nova)) && nova !== atual;
  const mismatch = confirma.length > 0 && confirma !== nova;
  const valid = atual.length > 0 && rulesOk && confirma === nova;
  const type = show ? 'text' : 'password';
  const error =
    change.error instanceof ApiRequestError
      ? change.error.message
      : change.error
        ? t.states.datasulDown
        : null;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    change.mutate(
      { 'senha-atual': atual, 'senha-nova': nova },
      {
        onSuccess: () => {
          setAtual('');
          setNova('');
          setConfirma('');
          setDone(true);
          onDone?.();
        },
      },
    );
  };

  return (
    <form onSubmit={onSubmit} className="stack" noValidate>
      <div className="field" style={{ maxWidth: 360 }}>
        <label htmlFor="pw-atual">{t.password.current}</label>
        <input
          id="pw-atual"
          type={type}
          autoComplete="current-password"
          value={atual}
          onChange={(e) => {
            setAtual(e.target.value);
            setDone(false);
          }}
        />
      </div>
      <div className="field" style={{ maxWidth: 360 }}>
        <label htmlFor="pw-nova">{t.password.next}</label>
        <input
          id="pw-nova"
          type={type}
          autoComplete="new-password"
          value={nova}
          aria-describedby="pw-rules"
          onChange={(e) => {
            setNova(e.target.value);
            setDone(false);
          }}
        />
      </div>
      <div id="pw-rules">
        <PasswordRules senha={nova} atual={atual} />
      </div>
      <div className="field" style={{ maxWidth: 360 }}>
        <label htmlFor="pw-confirma">{t.password.confirm}</label>
        <input
          id="pw-confirma"
          type={type}
          autoComplete="new-password"
          value={confirma}
          onChange={(e) => {
            setConfirma(e.target.value);
            setDone(false);
          }}
        />
        {mismatch ? <span className="hint bad">{t.password.mismatch}</span> : null}
      </div>
      <label className="check">
        <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
        <span>{t.password.show}</span>
      </label>
      {error ? (
        <div className="form-error" role="alert">
          {error}
        </div>
      ) : null}
      {done ? (
        <div className="banner ok" role="status">
          {t.password.done}
        </div>
      ) : null}
      <div>
        <button type="submit" className="btn" disabled={!valid || change.isPending}>
          {change.isPending ? t.password.submitting : (submitLabel ?? t.password.submit)}
        </button>
      </div>
    </form>
  );
}
